import { useState, useEffect } from 'react';
import { TrendingUp, DollarSign, Building2, Plus } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import AddItemModal from '../components/AddItemModal';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface Property {
  id: string;
  name: string;
  value: number;
  purchase_value: number;
  growth_rate: number;
  mortgage_amount: number | null;
  mortgage_rate: number | null;
  mortgage_term: number | null;
  details: {
    address: string;
    property_type: string;
    square_footage: number;
    year_built: number;
    last_appraisal_date: string;
    last_appraisal_value: number;
    rental_income: number;
    property_tax: number;
    insurance_cost: number;
    maintenance_cost: number;
  };
}

export default function RealEstate() {
  const { profile } = useAuth();
  const [properties, setProperties] = useState<Property[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    fetchProperties();
  }, []);

  async function fetchProperties() {
    try {
      setIsLoading(true);
      setError(null);
      const { data, error: propertiesError } = await supabase
        .from('assets')
        .select('*, details:property_details(*)')
        .eq('type', 'real_estate')
        .order('created_at', { ascending: false });
      if (propertiesError) throw propertiesError;
      setProperties(data || []);
    } catch {
      setError('Failed to load property data');
    } finally {
      setIsLoading(false);
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: profile?.currency || 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const calculateTotalValue = () => properties.reduce((total, prop) => total + prop.value, 0);
  const calculateTotalEquity = () =>
    properties.reduce((total, prop) => total + (prop.value - (prop.mortgage_amount || 0)), 0);
  const calculateTotalRentalIncome = () =>
    properties.reduce((total, prop) => total + (prop.details?.rental_income || 0), 0);

  if (isLoading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-center">
          <div className="h-16 w-16 animate-spin rounded-full border-2 border-slate-900 border-t-transparent mx-auto" />
          <p className="mt-4 text-slate-600">Loading property data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Real Estate</h1>
          <p className="text-sm text-slate-600">Manage your property portfolio</p>
        </div>
        <Button onClick={() => setShowAddModal(true)} className="gap-2 rounded-full bg-slate-900 text-white hover:bg-slate-800 font-semibold btn-hover-lift">
          <Plus className="w-5 h-5" />
          Add Property
        </Button>
      </div>

      {error && (
        <Alert variant="destructive" className="border-red-200 bg-red-50">
          <AlertDescription className="text-red-800">{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-l-4 border-l-slate-900">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-5 h-5 text-slate-700" />
              <h3 className="text-sm font-medium text-slate-600">Total Value</h3>
            </div>
            <p className="text-2xl font-bold text-slate-900">{formatCurrency(calculateTotalValue())}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-slate-600">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-slate-600" />
              <h3 className="text-sm font-medium text-slate-600">Total Equity</h3>
            </div>
            <p className="text-2xl font-bold text-slate-900">{formatCurrency(calculateTotalEquity())}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-slate-500">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-5 h-5 text-slate-600" />
              <h3 className="text-sm font-medium text-slate-600">Monthly Rental Income</h3>
            </div>
            <p className="text-2xl font-bold text-slate-900">{formatCurrency(calculateTotalRentalIncome())}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {properties.map((property) => (
          <Card key={property.id} className="overflow-hidden">
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold text-lg text-slate-900">{property.name}</h3>
                  <p className="text-sm text-slate-600">{property.details?.address}</p>
                </div>
                <span className="text-xs text-slate-500 capitalize">
                  {property.details?.property_type?.replace('_', ' ')}
                </span>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-600">Current Value</span>
                <span className="font-medium text-slate-900">{formatCurrency(property.value)}</span>
              </div>
              {property.mortgage_amount != null && property.mortgage_amount > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Mortgage Balance</span>
                  <span className="font-medium text-slate-900">{formatCurrency(property.mortgage_amount)}</span>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-600">Equity</span>
                <span className="font-medium text-slate-900">
                  {formatCurrency(property.value - (property.mortgage_amount || 0))}
                </span>
              </div>
              {property.details?.rental_income > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Monthly Rent</span>
                  <span className="font-medium text-slate-900">{formatCurrency(property.details.rental_income)}</span>
                </div>
              )}
            </CardContent>
            <div className="px-6 py-4 bg-slate-50/80 flex justify-between items-center border-t border-slate-200">
              <div className="text-sm text-slate-600">
                Last Appraised: {property.details?.last_appraisal_date ? new Date(property.details.last_appraisal_date).toLocaleDateString() : '-'}
              </div>
              <Button variant="link" size="sm" className="h-auto p-0 text-slate-700 hover:text-slate-900">
                View Details
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {showAddModal && (
        <AddItemModal onClose={() => setShowAddModal(false)} onSuccess={() => { setShowAddModal(false); fetchProperties(); }} />
      )}
    </div>
  );
}
